import { observer } from 'mobx-react';
import React, { useEffect, useMemo } from 'react';
import { Prompt, StaticContext } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';
import itemsStore from '../../store/itemsStore';
import ItemMainDetails from '../ItemMainDetails/ItemMainDetails';
import ItemOnlyForCombo from '../ItemOnlyForCombo/ItemOnlyForCombo';
import ItemModifiersAndExclusions from '../ItemModifiersAndExclusions/ItemModifiersAndExclusions';
import ItemsCrossSelling from '../ItemsCrossSelling/ItemsCrossSelling';
import ItemSizes from '../ItemSizes/ItemSizes';
import integrationStore from 'src/mobx/integrationStore';
import { localeStore } from 'src/mobx/localesStore';
import { scrollToErrorElement } from 'src/utils/helpers';
import { confirmZeroPrice } from 'src/utils/modalConfirmations';
import Button from 'src/view/components/Controls/ButtonComponent/Button';
import { getPromptProps } from 'src/view/components/CustomPrompt/helper';
import ItemImages from 'src/view/components/ItemImages/ItemImages';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { BUTTON_TITLE } from '~/constants/main';

type TProps = RouteComponentProps<{ id?: string }, StaticContext, undefined>;

const ItemsEdit: React.FC<TProps> = observer((props: TProps): JSX.Element => {
  const itemId = Number(props.match.params.id ?? 0);

  const isZeroPrice = useMemo((): boolean => {
    return Boolean(
      (!itemsStore.item?.is_size && Number(itemsStore.item?.price) === 0) ||
        (itemsStore.item?.is_size && itemsStore.item.sizes.find((size) => Number(size.price) === 0))
    );
  }, [itemsStore.item?.is_size, itemsStore.item?.price, itemsStore.item?.sizes]);

  useEffect(() => {
    itemsStore.fetchData(itemId);
  }, []);

  const clickSave = () => {
    if (!itemsStore.item) return;
    itemsStore.setValidation();

    if (!itemsStore.validation.valid) {
      scrollToErrorElement();
      return;
    }

    if (isZeroPrice) {
      confirmZeroPrice({ onConfirm: save });
      return;
    }

    save();
  };

  const save = () => {
    const { item, createItem, updateItem } = itemsStore;
    if (!item) return;

    if (item.is_draft) {
      createItem();
    } else {
      updateItem(item.id);
    }
  };

  return (
    <>
      <ScrollableBlock showScrollbar className="full-height mt-6">
        <ItemImages
          currentSaveImage={itemsStore.item?.image}
          currentSaveImages={itemsStore.item?.images}
          //
          images={itemsStore.images}
          validation={itemsStore.validation.images}
          draggableImage={itemsStore.draggableImage}
          posRestrictions={integrationStore.posRestrictions?.itemImages}
          uploadImage={itemsStore.uploadImage}
          deleteImage={itemsStore.deleteImage}
          imagesChangesSubmit={itemsStore.imagesChangesSubmit}
          setValidation={itemsStore.setValidation}
          setDraggableImage={itemsStore.setDraggableImage}
          handleDragEnd={itemsStore.handleDragEnd}
        />
        <ItemMainDetails />
        <ItemOnlyForCombo />
        <ItemSizes />
        <ItemModifiersAndExclusions />
        <ItemsCrossSelling />
        <Button title={localeStore.t(BUTTON_TITLE.SAVE)} onClick={clickSave} loading={itemsStore.isLoading} className="mt-8" />
      </ScrollableBlock>
      <LoadingOverlay visible={itemsStore.isLoading} />
      <Prompt when={itemsStore.edited} message={getPromptProps()} />
    </>
  );
});

export default ItemsEdit;
